"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { doc, getDoc, collection, getDocs, updateDoc, deleteDoc, query, increment } from "firebase/firestore"
import { auth, db } from "../../firebase"
import Navbar from "../../components/Navbar/Navbar"
import QueryHeader from "./QueryHeader"
import CommentCard from "./CommentCard"
import { MessageCircle, Filter, Send, ThumbsUp, ThumbsDown, Bot, X, ClipboardList } from "lucide-react"
import "./QueryDetails.css"
import VerdictChips from "../../components/Court/VerdictChips"
import AIJudgeBanner from "../../components/Court/AIJudgeBanner"
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import CommunityAIIcon from "../../components/AI/CommunityAIIcon"
import "../../components/AI/CommunityAIIcon.css"
import { v4 as uuidv4 } from 'uuid'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import ReportModal from '../../components/Reports/ReportModal'
import AIUsageModal from '../../components/AI/AIUsageModal'
import axios from 'axios'
import EditQueryModal from "../../components/EditQuery/EditQueryModal"
import { generateAIResponse, generateAICommentSummary, hasUnlockedAIFeatures, verifyApiClientSetup } from '../../components/AI/ai-utils'
import API_CONFIG from "../../config/api"
import { createVoteNotification, createCommentVoteNotification, createCommentNotification, createReplyNotification } from "../../utils/notificationUtils"
// AI utilities will be dynamically imported

function QueryDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [queryData, setQueryData] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState("")
  const [loading, setLoading] = useState(true)
  const loadingRef = useRef(true)
  useEffect(() => {
    loadingRef.current = loading
  }, [loading])
  const [activeImage, setActiveImage] = useState(null)
  const userId = auth.currentUser ? auth.currentUser.uid : null
  const user = auth.currentUser
  const [crowdfundingDetails, setCrowdfundingDetails] = useState(null)
  const [showResetButton, setShowResetButton] = useState(false)

  // States for editing and replying on comments and replies
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editText, setEditText] = useState("")
  const [replyingCommentId, setReplyingCommentId] = useState(null)
  const [replyText, setReplyText] = useState("")
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [editingReplyId, setEditingReplyId] = useState(null)
  const [editReplyText, setEditReplyText] = useState("")
  const [commentSortOption, setCommentSortOption] = useState("mostLiked")
  const [isPostingComment, setIsPostingComment] = useState(false)
  const [parentReplyId, setParentReplyId] = useState(null)

  // AI states
  const [aiSummary, setAiSummary] = useState("")
  const [isGeneratingAI, setIsGeneratingAI] = useState(false)
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false)
  const [showAIUsageModal, setShowAIUsageModal] = useState(false)
  const [aiUsageData, setAiUsageData] = useState(null)
  const [userPoints, setUserPoints] = useState(0)
  const [isFetchingCrowdfunding, setIsFetchingCrowdfunding] = useState(false)

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportData, setReportData] = useState(null)

  // Edit query modal state
  const [showEditModal, setShowEditModal] = useState(false)

  // Add state to track if a trial has been used for this query
  const [trialUsedForQuery, setTrialUsedForQuery] = useState(false)

  // AI response states
  const [aiResponse, setAiResponse] = useState("")
  const [aiResponseLoading, setAiResponseLoading] = useState(false)
  const [showAiResponse, setShowAiResponse] = useState(false)

  // AI summary states
  const [commentSummary, setCommentSummary] = useState("")
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [showSummary, setShowSummary] = useState(false)

  // AI feature states
  const [activeAIFeature, setActiveAIFeature] = useState(null)
  const [showAIModal, setShowAIModal] = useState(false)

  // Add a useEffect to show reset button after delay if still loading
  useEffect(() => {
    let timeoutId;
    
    if (loading) {
      timeoutId = setTimeout(() => {
        setShowResetButton(true);
      }, 15000); // Show reset button after 15 seconds
    } else {
      setShowResetButton(false);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading]);
  
  // Function to manually reload the page
  const handleManualReset = () => {
    // Force reload the current page
    window.location.reload();
  };

  // Function to fetch crowdfunding details
  const fetchCrowdfundingDetails = async (url) => {
    // Prevent duplicate calls
    if (isFetchingCrowdfunding) return;
    
    try {
      if (!url) {
        console.error("Missing crowdfunding URL");
        return;
      }

      // Set fetching state to true
      setIsFetchingCrowdfunding(true);
      
      // Show toast notification that we're loading the details with a unique ID
      toast.info("Loading crowdfunding details...", { 
        toastId: "loading-crowdfunding",
        autoClose: false
      });
      
      const response = await axios.post(API_CONFIG.getUrl("api/crowdfundingDetails"), { 
        crowdfundingUrl: url 
      });
      
      // Dismiss the loading toast regardless of outcome
      toast.dismiss("loading-crowdfunding");
      
      if (response.data.success && response.data.data) {
        // Validate the data to ensure it has the required fields
        const details = response.data.data;
        if (!details.title || (!details.raised && details.raised !== 0) || (!details.goal && details.goal !== 0)) {
          console.warn("Incomplete crowdfunding data received:", details);
          toast.warning("Incomplete crowdfunding information retrieved.", {
            toastId: "crowdfunding-incomplete"
          });
          setIsFetchingCrowdfunding(false);
          return;
        }
        
        // Update local state
        setCrowdfundingDetails(details);
        
        // Also update the query document to save the crowdfunding details
        const queryDocRef = doc(db, "queries", id);
        await updateDoc(queryDocRef, {
          crowdfundingSummary: details
        });
        
        // Update local queryData state
        setQueryData(prev => ({
          ...prev,
          crowdfundingSummary: details
        }));
        
        toast.success("Crowdfunding details updated", {
          toastId: "crowdfunding-updated"
        });
      } else {
        console.error("Error fetching crowdfunding details:", response.data.error || "Unknown error");
        toast.error("Unable to load crowdfunding details. Please try again later.", {
          toastId: "crowdfunding-error"
        });
      }
    } catch (err) {
      console.error("Error fetching crowdfunding details:", err);
      toast.error("Failed to fetch crowdfunding information. Please check the URL and try again.", {
        toastId: "crowdfunding-error"
      });
    } finally {
      // Reset fetching state regardless of outcome
      setIsFetchingCrowdfunding(false);
    }
  };

  // Update the fetchComments function to fetch replies and format them correctly
  const fetchComments = useCallback(async () => {
    setLoading(true);
    
    try {
      // Check if id exists before proceeding
      if (!id) {
        console.error("Query ID is undefined");
        toast.error("Invalid query ID");
        setLoading(false);
        return;
      }
      
      // Get docket or query data (check cases collection first, fallback to queries)
      let docRef = doc(db, "cases", id);
      let docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        docRef = doc(db, "queries", id);
        docSnap = await getDoc(docRef);
      }
      
      if (!docSnap.exists()) {
        console.error("Docket not found");
        toast.error("Docket not found");
        setLoading(false);
        return;
      }
      
      const queryData = docSnap.data();
      
      let allComments = [];
      
      // Check if comments exist in the new structure (embedded in the query document)
      if (queryData.comments && Array.isArray(queryData.comments)) {
        // Deep clone to avoid reference issues
        const newStructureComments = JSON.parse(JSON.stringify(queryData.comments));
        
        // Format comments with owner ID
        allComments = newStructureComments.map(comment => ({
          ...comment,
          queryOwnerId: queryData?.createdBy || "",
          dataSource: "new" // Mark as new structure
        }));
      } else {
        // If no comments found in new structure, check for old structure
        // This block can be removed in the future when all data is migrated
        try {
          const commentsRef = collection(db, "queries", id, "comments");
          const qSnap = await getDocs(query(commentsRef));
          
          if (!qSnap.empty) {
            // Convert old-structure comments to new structure format
            const oldStructureComments = await Promise.all(
              qSnap.docs.map(async (docSnap) => {
                const commentData = { 
                  id: docSnap.id, 
                  ...docSnap.data(), 
                  queryOwnerId: queryData?.createdBy || "",
                  dataSource: "old" // Mark as old structure for tracking
                };
                
                // Get replies for this comment
                try {
                  const repliesRef = collection(db, "queries", id, "comments", docSnap.id, "replies");
                  const repliesSnap = await getDocs(query(repliesRef));
                  
                  if (!repliesSnap.empty) {
                    commentData.replies = repliesSnap.docs.map((r) => ({ 
                      id: r.id, 
                      ...r.data(),
                      dataSource: "old" 
                    }));
                  } else {
                    commentData.replies = [];
                  }
                } catch (err) {
                  console.error(`Error fetching replies for comment ${docSnap.id}:`, err);
                  commentData.replies = [];
                }
                
                return commentData;
              })
            );
            
            allComments = oldStructureComments;
            
            // Migrate old comments to new structure
            try {
              // Only do this if we need to migrate data
              if (oldStructureComments.length > 0) {
                // Update the query document to include these comments
                await updateDoc(docRef, {
                  comments: oldStructureComments.map(c => {
                    const { dataSource, queryOwnerId, ...commentData } = c;
                    return commentData;
                  })
                });
              }
            } catch (migrationErr) {
              console.error("Error migrating comments:", migrationErr);
              // Don't set loading to false here as we still have data to display
            }
          }
        } catch (err) {
          console.error("Error fetching old-structure comments:", err);
          // Don't set loading to false here as we still have the query data
        }
      }

      // Sort comments based on the current sort option
      allComments = sortComments(allComments, commentSortOption);
      
      // Set local state
      setComments(allComments);
      setQueryData({ ...queryData, id: id });
      
      // Fetch crowdfunding details if this query has a crowdfunding link and details haven't been loaded yet
      if (queryData.crowdfundingLink && !queryData.crowdfundingSummary && !isFetchingCrowdfunding) {
        fetchCrowdfundingDetails(queryData.crowdfundingLink);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching comments:", error);
      // Add more detailed error information
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
        queryId: id
      });
      
      // Ensure we set queryData to at least have an ID to prevent infinite loading
      if (!queryData) {
        setQueryData({ id: id });
      }
      
      setLoading(false);
      toast.error("Error loading query. Please try again.");
    }
  }, [id, commentSortOption, isFetchingCrowdfunding]);

  // Remove the other useEffect for queryData since we're now fetching everything at once
  useEffect(() => {
    // Make sure id is valid
    if (id) {
      fetchComments();
      
      // Add a safety timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        if (loadingRef.current) {
          setLoading(false);
          setQueryData(prevData => prevData || { id: id, error: "timeout" });
          toast.warning("Query data took too long to load. Some data might be missing.");
        }
      }, 12000); // 12 second timeout
      
      return () => clearTimeout(timeoutId);
    } else {
      setQueryData({ error: "invalid-id" });
      setLoading(false);
    }
  }, [fetchComments, id]);

  // Add useEffect to redirect if queryId is invalid
  useEffect(() => {
    if (queryData?.error === "invalid-id") {
      toast.error("Invalid query ID. Redirecting to home page.");
      setTimeout(() => {
        navigate("/");
      }, 2000);
    }
  }, [queryData, navigate]);

  // Fetch user points when component loads
  useEffect(() => {
    const fetchUserPoints = async () => {
      if (!userId) return;
      
      try {
        const userRef = doc(db, "users", userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserPoints(userData.points || 0);
        }
      } catch (error) {
        console.error("Error fetching user points:", error);
      }
    };

    fetchUserPoints();
  }, [userId]);

  // Check if a trial has been used for this query when component mounts
  useEffect(() => {
    if (id && user?.uid) {
      try {
        // Get trial queries from localStorage
        const storedTrialQueries = localStorage.getItem('ai_trial_queries');
        if (storedTrialQueries) {
          const trialQueries = JSON.parse(storedTrialQueries);
          
          // Check if current queryId exists in the stored trials
          const hasUsedTrial = trialQueries.some(q => 
            q.queryId === id && q.userId === user.uid
          );
          
          if (hasUsedTrial) {
            console.log(`Found existing trial usage for query ${id}`);
            setTrialUsedForQuery(true);
          }
        }
      } catch (error) {
        console.error("Error checking stored trial usage:", error);
      }
    }
  }, [id, user?.uid]);

  // Monitor changes in AI response state
  useEffect(() => {
    if (aiResponse) {
      setShowAiResponse(!!aiResponse);
    }
  }, [aiResponse, showAiResponse]);

  // Monitor changes in comment summary state
  useEffect(() => {
    if (commentSummary) {
      setShowSummary(!!commentSummary);
    }
  }, [commentSummary, showSummary]);

  // Make sure API client is properly set up on component load
  useEffect(() => {
    const verifyApi = async () => {
      try {
        await verifyApiClientSetup();
      } catch (error) {
        console.warn("Error verifying API client:", error);
      }
    };
    
    verifyApi();
  }, []);

  // ---------- Sorting Logic for Comments ----------
  const sortComments = (commentList, sortOption) => {
    if (!commentList) return []

    // Create a copy of the comment list to avoid mutating the original
    const sortedList = [...commentList]

    // First separate AI comments
    const aiComments = sortedList.filter((comment) => comment.isAIResponse)
    const normalComments = sortedList.filter((comment) => !comment.isAIResponse)

    // Sort normal comments according to the selected option
    let sortedNormalComments

    switch (sortOption) {
      case "newest":
        sortedNormalComments = normalComments.sort((a, b) => {
          // Handle createdAt as ISO strings or Timestamps
          const dateA = a.createdAt ? (typeof a.createdAt === 'string' ? new Date(a.createdAt) : a.createdAt.toDate()) : new Date(0)
          const dateB = b.createdAt ? (typeof b.createdAt === 'string' ? new Date(b.createdAt) : b.createdAt.toDate()) : new Date(0)
          return dateB - dateA
        })
        break
      case "oldest":
        sortedNormalComments = normalComments.sort((a, b) => {
          // Handle createdAt as ISO strings or Timestamps
          const dateA = a.createdAt ? (typeof a.createdAt === 'string' ? new Date(a.createdAt) : a.createdAt.toDate()) : new Date(0)
          const dateB = b.createdAt ? (typeof b.createdAt === 'string' ? new Date(b.createdAt) : b.createdAt.toDate()) : new Date(0)
          return dateA - dateB
        })
        break
      case "engagement":
        sortedNormalComments = normalComments.sort((a, b) => {
          const aEngagement = a.replies?.length || 0
          const bEngagement = b.replies?.length || 0
          return bEngagement - aEngagement
        })
        break
      case "mostLiked":
      default:
        sortedNormalComments = normalComments.sort((a, b) => {
          // Calculate score (upvotes - downvotes)
          const aScore = (a.upvoteCount || 0) - (a.downvoteCount || 0)
          const bScore = (b.upvoteCount || 0) - (b.downvoteCount || 0)

          // If scores are equal, sort by engagement
          if (bScore === aScore) {
            const aEngagement = a.replies?.length || 0
            const bEngagement = b.replies?.length || 0
            return bEngagement - aEngagement
          }

          return bScore - aScore
        })
        break
    }

    // Always return AI comments first, followed by sorted normal comments
    return [...aiComments, ...sortedNormalComments]
  }

  // ---------- Voting Logic for Query ----------
  const updateVote = async (docRef, type, currentVote, userId, setState) => {
    const docSnap = await getDoc(docRef)
    if (!docSnap.exists()) return
    const data = docSnap.data()
    const votes = data.votes || {}
    let newUpvoteCount = data.upvoteCount || 0
    let newDownvoteCount = data.downvoteCount || 0
    const newVotes = { ...votes }
    let shouldSendNotification = false
    let isUpvote = false

    if (type === "upvote") {
      if (currentVote === "upvote") {
        newUpvoteCount -= 1
        delete newVotes[userId]
        shouldSendNotification = false
      } else {
        newUpvoteCount += 1
        if (currentVote === "downvote") newDownvoteCount -= 1
        newVotes[userId] = "upvote"
        shouldSendNotification = currentVote !== "upvote"
        isUpvote = true
      }
    } else if (type === "downvote") {
      if (currentVote === "downvote") {
        newDownvoteCount -= 1
        delete newVotes[userId]
        shouldSendNotification = false
      } else {
        newDownvoteCount += 1
        if (currentVote === "upvote") newUpvoteCount -= 1
        newVotes[userId] = "downvote"
        shouldSendNotification = currentVote !== "downvote"
        isUpvote = false
      }
    }

    await updateDoc(docRef, {
      upvoteCount: Math.max(0, newUpvoteCount),
      downvoteCount: Math.max(0, newDownvoteCount),
      votes: newVotes,
    })

    setState((prev) => ({
      ...prev,
      upvoteCount: Math.max(0, newUpvoteCount),
      downvoteCount: Math.max(0, newDownvoteCount),
      votes: newVotes,
    }))
    
    // Send notification to query author if this is a new vote or changed vote type
    if (data.createdBy !== userId) {
      try {
        const user = auth.currentUser
        const userName = user?.displayName || 'A user'
        
        createVoteNotification(
          data.createdBy,
          userName,
          docRef.id,
          data.title || 'a query',
          isUpvote
        )
      } catch (error) {
        console.error("Error sending vote notification:", error)
      }
    }
  }

  const handleVote = async (type) => {
    if (!auth.currentUser) return
    if (!id) {
      toast.error("Invalid query ID");
      return;
    }
    const docRef = doc(db, "queries", id)
    const currentVote = queryData.votes ? queryData.votes[userId] : null
    await updateVote(docRef, type, currentVote, userId, setQueryData)
    
    // Immediately refresh the query data to ensure votes are properly reflected
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const freshData = docSnap.data();
        setQueryData(prev => ({
          ...prev,
          ...freshData,
        }));
      }
    } catch (error) {
      console.error("Error refreshing query data:", error);
    }
  }

  // ---------- Voting Logic for Comments ----------
  const updateVoteForComment = async (docRef, type, currentVote, userId, previousComment) => {
    const docSnap = await getDoc(docRef)
    if (!docSnap.exists()) return previousComment
    const data = docSnap.data()

    const existingData = { ...previousComment, ...data }
    let newUpvoteCount = existingData.upvoteCount ?? 0
    let newDownvoteCount = existingData.downvoteCount ?? 0
    const newVotes = { ...existingData.votes }

    if (type === "upvote") {
      if (!currentVote) {
        newUpvoteCount += 1
        newVotes[userId] = "upvote"
      } else if (currentVote === "upvote") {
        newUpvoteCount -= 1
        delete newVotes[userId]
      } else if (currentVote === "downvote") {
        newDownvoteCount -= 1
        newUpvoteCount += 1
        newVotes[userId] = "upvote"
      }
    } else if (type === "downvote") {
      if (!currentVote) {
        newDownvoteCount += 1
        newVotes[userId] = "downvote"
      } else if (currentVote === "downvote") {
        newDownvoteCount -= 1
        delete newVotes[userId]
      } else if (currentVote === "upvote") {
        newUpvoteCount -= 1
        newDownvoteCount += 1
        newVotes[userId] = "downvote"
      }
    }

    await updateDoc(docRef, {
      upvoteCount: Math.max(0, newUpvoteCount),
      downvoteCount: Math.max(0, newDownvoteCount),
      votes: newVotes,
    })

    return {
      ...existingData,
      upvoteCount: Math.max(0, newUpvoteCount),
      downvoteCount: Math.max(0, newDownvoteCount),
      votes: newVotes,
    }
  }

  async function handleCommentVote(commentId, type) {
    if (!auth.currentUser) {
      toast.error("Please log in to vote");
      return;
    }
    
    if (!id) {
      toast.error("Invalid query ID");
      return;
    }
    
    try {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) return;
      
      // Don't allow self-voting
      if (comment.createdBy === userId) {
        toast.warning("You cannot vote on your own comments");
        return;
      }
      
      const isUpvote = type === "upvote";
      const currentVote = comment.votes ? comment.votes[userId] : null;
      let shouldSendNotification = false;
      
      // Determine if we should send a notification based on vote change
      if (isUpvote && currentVote !== "upvote") {
        shouldSendNotification = true;
      } else if (!isUpvote && currentVote !== "downvote") {
        shouldSendNotification = true;
      }
      
      // Check if this is an old structure comment
      if (comment.dataSource === "old") {
        // Update in the comments subcollection
        const commentRef = doc(db, "queries", id, "comments", commentId);
        const updatedComment = await updateVoteForComment(commentRef, type, currentVote, userId, comment);
        
        // Update local state
        setComments(prev => {
          const newComments = prev.map(c => 
            c.id === commentId ? updatedComment : c
          );
          return sortComments(newComments, commentSortOption);
        });
        
        // Send notification if needed
        if (comment.createdBy !== userId) {
          const user = auth.currentUser;
          const userName = user?.displayName || 'A user';
          
          createCommentVoteNotification(
            comment.createdBy,
            userName,
            id,
            queryData.title || 'a query',
            isUpvote
          ).catch(err => console.error("Error sending comment vote notification:", err));
        }
      } else {
        // Update in the query document's comments array
        const queryRef = doc(db, "queries", id);
        const querySnapshot = await getDoc(queryRef);
        
        if (!querySnapshot.exists()) {
          throw new Error("Query not found");
        }
        
        const commentsData = querySnapshot.data().comments || [];
        const updatedComments = commentsData.map(c => {
          if (c.id === commentId) {
            const currentVote = c.votes ? c.votes[userId] : null;
            let newUpvoteCount = c.upvoteCount || 0;
            let newDownvoteCount = c.downvoteCount || 0;
            const newVotes = { ...(c.votes || {}) };

            if (type === "upvote") {
              if (!currentVote) {
                newUpvoteCount += 1;
                newVotes[userId] = "upvote";
              } else if (currentVote === "upvote") {
                newUpvoteCount -= 1;
                delete newVotes[userId];
              } else if (currentVote === "downvote") {
                newDownvoteCount -= 1;
                newUpvoteCount += 1;
                newVotes[userId] = "upvote";
              }
            } else if (type === "downvote") {
              if (!currentVote) {
                newDownvoteCount += 1;
                newVotes[userId] = "downvote";
              } else if (currentVote === "downvote") {
                newDownvoteCount -= 1;
                delete newVotes[userId];
              } else if (currentVote === "upvote") {
                newUpvoteCount -= 1;
                newDownvoteCount += 1;
                newVotes[userId] = "downvote";
              }
            }

            return {
              ...c,
              upvoteCount: Math.max(0, newUpvoteCount),
              downvoteCount: Math.max(0, newDownvoteCount),
              votes: newVotes
            };
          }
          return c;
        });

        // Update in Firestore
        await updateDoc(queryRef, {
          comments: updatedComments
        });
        
        // Update local state
        setQueryData(prev => ({
          ...prev,
          comments: updatedComments
        }));
        
        setComments(sortComments(updatedComments, commentSortOption));
        
        // Send notification if needed
        if (comment.createdBy !== userId) {
          const user = auth.currentUser;
          const userName = user?.displayName || 'A user';
          
          createCommentVoteNotification(
            comment.createdBy,
            userName,
            id,
            queryData.title || 'a query',
            isUpvote
          ).catch(err => console.error("Error sending comment vote notification:", err));
        }
      }
    } catch (err) {
      console.error("Error updating comment vote:", err);
      toast.error("Failed to update vote. Please try again.");
    }
  }

  const handleReplyVote = async (commentId, replyId, voteType, parentReplyId = null) => {
    if (!auth.currentUser) {
      toast.error("Please log in to vote");
      return;
    }

    if (!id) {
      toast.error("Invalid query ID");
      return;
    }

    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) {
        throw new Error("Comment not found");
      }

      // Check if this is an old structure comment
      if (comment.dataSource === "old") {
        // Update in the replies subcollection
        const replyRef = doc(db, "queries", id, "comments", commentId, "replies", replyId);
        const currentVote = comment.replies?.find(r => r.id === replyId)?.votes?.[userId] || null;
        
        // Update the reply's vote count
        const reply = comment.replies.find(r => r.id === replyId);
        const updatedReply = updateVoteCount(reply, voteType, userId);
        
        // Update in Firestore
        await updateDoc(replyRef, {
          upvoteCount: updatedReply.upvoteCount,
          downvoteCount: updatedReply.downvoteCount,
          votes: updatedReply.votes
        });
        
        // Update local state
        setComments(prev => {
          const newComments = prev.map(c => {
            if (c.id === commentId) {
              return {
                ...c,
                replies: c.replies.map(r => 
                  r.id === replyId ? updatedReply : r
                )
              };
            }
            return c;
          });
          return sortComments(newComments, commentSortOption);
        });
      } else {
        // Update in the query document's comments array
        const queryRef = doc(db, "queries", id);
        const querySnapshot = await getDoc(queryRef);
        
        if (!querySnapshot.exists()) {
          throw new Error("Query not found");
        }
        
        const commentsData = querySnapshot.data().comments || [];
        const updatedComments = commentsData.map(c => {
          if (c.id === commentId) {
            // Function to recursively find and update the reply
            const findAndUpdateReply = (replies) => {
              for (let i = 0; i < replies.length; i++) {
                if (replies[i].id === replyId) {
                  // Found the reply to update
                  replies[i] = updateVoteCount(replies[i], voteType, userId);
                  return true;
                }
                
                // Check nested replies
                if (replies[i].replies && replies[i].replies.length > 0) {
                  if (findAndUpdateReply(replies[i].replies)) {
                    return true;
                  }
                }
              }
              return false;
            };
            
            // Update the comment's replies
            if (c.replies && c.replies.length > 0) {
              findAndUpdateReply(c.replies);
            }
          }
          return c;
        });

        // Update in Firestore
        await updateDoc(queryRef, {
          comments: updatedComments
        });
        
        // Update local state
        setQueryData(prev => ({
          ...prev,
          comments: updatedComments,
        }));
        
        setComments(sortComments(updatedComments, commentSortOption));
      }
    } catch (error) {
      console.error("Error voting on reply:", error);
      toast.error("Failed to vote. Please try again.");
    }
  };

  // ---------- Post a Comment ----------
  const handlePostComment = async () => {
    if (!newComment.trim() || !auth.currentUser) return
    if (!id) {
      toast.error("Invalid query ID");
      return;
    }

    try {
      setIsPostingComment(true)
      
      // Get user badge if available
      let commenterBadge = null;
      let commenterBadgeTitle = null;
      
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          commenterBadge = userData.displayBadge || null;
          commenterBadgeTitle = userData.activeBadge || null;
        }
      } catch (error) {
        console.error("Error fetching user badge:", error);
      }
      
      // Create new comment object
      const newCommentObj = {
        id: uuidv4(), // Generate a unique ID
        text: newComment,
        upvoteCount: 0,
        downvoteCount: 0,
        votes: {},
        createdAt: new Date().toISOString(),
        commenterName: auth.currentUser.displayName || "Anonymous",
        commenterPhoto: auth.currentUser.photoURL || "",
        commenterBadge: commenterBadge,
        commenterBadgeTitle: commenterBadgeTitle,
        createdBy: userId,
        replies: [], // Initialize empty replies array
        dataSource: "new" // Mark as new structure
      }
      
      // Get the query document
      const queryRef = doc(db, "queries", id)
      const querySnapshot = await getDoc(queryRef)
      
      if (!querySnapshot.exists()) {
        throw new Error("Query not found")
      }
      
      // Update the query document with the new comment (only in the new structure)
      const updatedDbComments = [...(queryData.comments || []), newCommentObj]
      
      await updateDoc(queryRef, {
        comments: updatedDbComments
      })
      
      // Send notification to query owner if it's not the same person
      if (queryData.createdBy && queryData.createdBy !== userId) {
        try {
          const userName = auth.currentUser.displayName || "Anonymous";
          
          createCommentNotification(
            queryData.createdBy,
            userName,
            id,
            queryData.title || 'a query'
          ).catch(err => console.error("Error sending comment notification:", err));
        } catch (error) {
          console.error("Error sending notification:", error);
        }
      }
      
      // Update query data with new comment
      setQueryData({
        ...queryData,
        comments: updatedDbComments
      })
      
      // Update full comments list (combined old and new structure)
      setComments(prev => {
        return sortComments([
          ...prev,
          {
            ...newCommentObj,
            queryOwnerId: queryData.createdBy || "",
          }
        ], commentSortOption)
      })
      
      // Clear input
      setNewComment("")
      
      // Update user comment count for points
      incrementUserCommentCount(userId)
      
      toast.success("Comment posted successfully!")
    } catch (error) {
      console.error("Error posting comment:", error)
      toast.error("Failed to post comment. Please try again.")
    } finally {
      setIsPostingComment(false)
    }
  }

  // ---------- Post a Reply ----------
  const handlePostReply = async (commentId, replyText, parentReplyId = null) => {
    if (!user) {
      toast.error("Please log in to post a reply");
      return;
    }

    if (!replyText.trim()) return;
    
    if (!id) {
      toast.error("Invalid query ID");
      return;
    }

    try {
      // Extract @username from reply text for replyingTo field
      let replyingTo = '';
      const atMatch = replyText.match(/^@([^:]+):/);
      if (atMatch) {
        replyingTo = atMatch[1];
      }
      
      // Get user badge if available
      let replierBadge = null;
      let replierBadgeTitle = null;
      
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          replierBadge = userData.displayBadge || null;
          replierBadgeTitle = userData.activeBadge || null;
        }
      } catch (error) {
        console.error("Error fetching user badge:", error);
      }
      
      // Create new reply object
      const newReply = {
        id: uuidv4(),
        text: replyText,
        upvoteCount: 0,
        downvoteCount: 0,
        votes: {},
        createdAt: new Date().toISOString(),
        replierName: user.displayName || "Anonymous",
        replierPhoto: user.photoURL || "",
        replierBadge: replierBadge,
        replierBadgeTitle: replierBadgeTitle,
        createdBy: userId,
        replyingTo,
        replies: []
      };
      
      // Get the comment we're replying to
      const comment = comments.find(c => c.id === commentId);
      
      if (!comment) {
        throw new Error("Comment not found");
      }
      
      // Find parent reply if applicable (for notification purposes)
      let parentReply = null;
      if (parentReplyId) {
        const findParentReply = (replies) => {
          for (const reply of replies || []) {
            if (reply.id === parentReplyId) {
              return reply;
            }
            
            if (reply.replies && reply.replies.length > 0) {
              const found = findParentReply(reply.replies);
              if (found) return found;
            }
          }
          return null;
        };
        
        parentReply = findParentReply(comment.replies);
      }
      
      // Query reference
      const queryRef = doc(db, "queries", id);
      
      // Update firestore comments array
      const querySnapshot = await getDoc(queryRef);
      
      if (!querySnapshot.exists()) {
        throw new Error("Query not found");
      }
      
      const updatedComments = [...querySnapshot.data().comments || []];
      
      // Find the comment and add the reply to it
      for (let i = 0; i < updatedComments.length; i++) {
        if (updatedComments[i].id === commentId) {
          const comment = updatedComments[i];
          
          // If there's a parentReplyId, it's a nested reply
          if (parentReplyId) {
            // Find the parent reply recursively
            const findAndAddNestedReply = (replies) => {
              for (let j = 0; j < replies.length; j++) {
                if (replies[j].id === parentReplyId) {
                  // Add as a nested reply to this parent
                  if (!replies[j].replies) {
                    replies[j].replies = [];
                  }
                  replies[j].replies.push(newReply);
                  return true;
                }
                
                // Recursively check nested replies
                if (replies[j].replies && replies[j].replies.length > 0) {
                  const found = findAndAddNestedReply(replies[j].replies);
                  if (found) return true;
                }
              }
              return false;
            };
            
            findAndAddNestedReply(comment.replies || []);
          } else {
            // Direct reply to the comment
            if (!comment.replies) comment.replies = [];
            comment.replies.push(newReply);
          }
          break;
        }
      }
  
      await updateDoc(queryRef, {
        comments: updatedComments,
      });
  
      // Update the local state - first update queryData
      setQueryData((prevData) => ({
        ...prevData,
        comments: updatedComments,
      }));
      
      // Then update the comments array which contains both old and new comments
      const combinedComments = comments.map(c => {
        if (c.id === commentId) {
          // Make a deep copy of the comment
          const updatedComment = JSON.parse(JSON.stringify(c));
          
          // If there's a parentReplyId, it's a nested reply
          if (parentReplyId) {
            // Find the parent reply recursively
            const findAndAddNestedReply = (replies) => {
              for (let j = 0; j < replies.length; j++) {
                if (replies[j].id === parentReplyId) {
                  // Add as a nested reply to this parent
                  if (!replies[j].replies) {
                    replies[j].replies = [];
                  }
                  replies[j].replies.push(newReply);
                  return true;
                }
                
                // Recursively check nested replies
                if (replies[j].replies && replies[j].replies.length > 0) {
                  const found = findAndAddNestedReply(replies[j].replies);
                  if (found) return true;
                }
              }
              return false;
            };
            
            findAndAddNestedReply(updatedComment.replies || []);
          } else {
            // Direct reply to the comment
            if (!updatedComment.replies) updatedComment.replies = [];
            updatedComment.replies.push(newReply);
          }
          
          return updatedComment;
        }
        return c;
      });
      
      // Send notification to the comment owner if it's not the same person
      if (comment.createdBy && comment.createdBy !== userId) {
        try {
          const userName = user.displayName || "Anonymous";
          
          createReplyNotification(
            comment.createdBy,
            userName,
            id,
            queryData.title || 'a query'
          ).catch(err => console.error("Error sending reply notification:", err));
        } catch (error) {
          console.error("Error sending notification:", error);
        }
      }
      
      // If replying to a reply (nested), also notify the parent reply owner
      if (parentReply && parentReply.createdBy && parentReply.createdBy !== userId && parentReply.createdBy !== comment.createdBy) {
        try {
          const userName = user.displayName || "Anonymous";
          
          createReplyNotification(
            parentReply.createdBy,
            userName,
            id,
            queryData.title || 'a query'
          ).catch(err => console.error("Error sending nested reply notification:", err));
        } catch (error) {
          console.error("Error sending nested reply notification:", error);
        }
      }
      
      // Sort the combined comments
      setComments(sortComments(combinedComments, commentSortOption));
      
      // Clear reply state
      setReplyingCommentId(null);
      setReplyText("");
      
      // Increment user comment count for points
      incrementUserCommentCount(userId);
      
      toast.success("Reply posted successfully!");
    } catch (error) {
      console.error("Error posting reply:", error);
      toast.error("Failed to post reply. Please try again.");
    }
  };

  // ---------- Edit Comment ----------
  const handleEditComment = async (commentId, updatedText) => {
    if (!updatedText.trim() || !auth.currentUser) return;
    
    try {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) return;

      // Check if this is an old structure comment
      if (comment.dataSource === "old") {
        // Update in the comments subcollection
        const commentRef = doc(db, "queries", id, "comments", commentId);
        await updateDoc(commentRef, {
          text: updatedText,
          edited: true,
          editedAt: new Date().toISOString()
        });
        
        // Update local state
        setComments(prev => {
          const newComments = prev.map(c => 
            c.id === commentId 
              ? { ...c, text: updatedText, edited: true, editedAt: new Date().toISOString() }
              : c
          );
          return sortComments(newComments, commentSortOption);
        });
      } else {
        // Update in the query document's comments array
        const queryRef = doc(db, "queries", id);
        const querySnapshot = await getDoc(queryRef);
        
        if (!querySnapshot.exists()) {
          throw new Error("Query not found");
        }
        
        const queryData = querySnapshot.data();
        const commentsData = queryData.comments || [];
        const updatedComments = commentsData.map(c => 
          c.id === commentId 
            ? { 
                ...c, 
                text: updatedText, 
                edited: true, 
                editedAt: new Date().toISOString() 
              }
            : c
        );

        // Update in Firestore
        await updateDoc(queryRef, {
          comments: updatedComments
        });
        
        // Update both states to ensure consistency
        setQueryData(prev => ({
          ...prev,
          comments: updatedComments
        }));
        
        
        setComments(prev => {
          const newComments = prev.map(c => 
            c.id === commentId 
              ? { 
                  ...c, 
                  text: updatedText, 
                  edited: true, 
                  editedAt: new Date().toISOString() 
                }
              : c
          );
          return sortComments(newComments, commentSortOption);
        });
      }
      
      // Reset editing state
      setEditingCommentId(null);
      setEditText("");
      
      toast.success("Comment updated successfully");
    } catch (err) {
      console.error("Error editing comment:", err);
      toast.error("Failed to edit comment. Please try again.");
    }
  }

  // ---------- Delete Comment ----------
  const handleDeleteComment = async (commentId) => {
    if (!id) {
      toast.error("Invalid query ID");
      return;
    }
    
    try {
      // Get current query document
      const queryRef = doc(db, "queries", id);
      const querySnap = await getDoc(queryRef);
      
      if (!querySnap.exists()) {
        toast.error("Query not found");
        return;
      }
      
      const queryData = querySnap.data();
      
      // Filter out the comment to delete
      const updatedComments = (queryData.comments || []).filter(comment => comment.id !== commentId);
      
      // Update the query document with the filtered comments
      await updateDoc(queryRef, {
        comments: updatedComments
      });
      
      // Update local state
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
      
      toast.success("Comment deleted successfully");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Error deleting comment. Please try again.");
    }
  };

  // ---------- Larger Image Modal ----------
  const handleImageClick = (url) => {
    // Make sure the URL is valid before showing in modal
    if (url && typeof url === 'string') {
      // Show the clicked image in a modal
      setActiveImage(url);
    }
  }

  const closeModal = (e) => {
    // Only close if clicking the backdrop, not the image
    if (e.target.classList.contains('image-modal')) {
      setActiveImage(null);
    }
  }

  const forceCloseModal = () => {
    setActiveImage(null);
  }

  // ---------- Sorting Dropdown for Comments ----------
  const handleSortChange = (e) => {
    setCommentSortOption(e.target.value)
    setComments(sortComments([...comments], e.target.value))
  }

  // Handle editing a reply
  const handleEditReply = async (commentId, replyId, editedText, parentReplyId = null) => {
    if (!user || !editedText.trim()) return;

    try {
      setLoading(true);
      const queryRef = doc(db, "queries", id);
      const querySnapshot = await getDoc(queryRef);

      if (!querySnapshot.exists()) {
        throw new Error("Query not found");
      }

      const commentsData = querySnapshot.data().comments || [];
      // Deep copy to avoid mutation issues
      const updatedComments = JSON.parse(JSON.stringify(commentsData));
      
      // Find the comment and update the reply
      for (let i = 0; i < updatedComments.length; i++) {
        const comment = updatedComments[i];
        
        if (comment.id === commentId) {
          // Function to recursively search and edit the reply
          const findAndEditReply = (replies) => {
            // First check direct children
            for (let j = 0; j < replies.length; j++) {
              if (replies[j].id === replyId) {
                // Found the reply to edit
                replies[j] = {
                  ...replies[j],
                  text: editedText,
                  edited: true,
                  editedAt: new Date().toISOString()
                };
                return true;
              }
            }
            
            // If not found, recursively check nested replies
            for (let j = 0; j < replies.length; j++) {
              if (replies[j].replies && replies[j].replies.length > 0) {
                const found = findAndEditReply(replies[j].replies);
                if (found) return true;
              }
            }
            
            return false;
          };
          
          if (comment.replies && comment.replies.length > 0) {
            findAndEditReply(comment.replies);
          }
          
          break;
        }
      }

      await updateDoc(queryRef, {
        comments: updatedComments,
      });

      // Update the local state
      setQueryData((prevData) => ({
        ...prevData,
        comments: updatedComments,
      }));
      
      setEditingReplyId(null);
      setEditReplyText("");
      toast.success("Reply updated successfully");
    } catch (error) {
      console.error("Error editing reply:", error);
      toast.error("Failed to edit reply. Please try again.");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a reply
  const handleDeleteReply = async (commentId, replyId, parentReplyId = null) => {
    try {
      // Get current query document
      const queryRef = doc(db, "queries", id);
      const querySnap = await getDoc(queryRef);
      
      if (!querySnap.exists()) {
        toast.error("Query not found");
        return;
      }
      
      const queryData = querySnap.data();
      const comments = queryData.comments || [];
      
      // Find the comment
      const commentIndex = comments.findIndex(comment => comment.id === commentId);
      
      if (commentIndex === -1) {
        toast.error("Comment not found");
        return;
      }
      
      // Create a deep copy of the comments to avoid mutation issues
      const updatedComments = JSON.parse(JSON.stringify(comments));
      const comment = updatedComments[commentIndex];
      
      // If no parent reply, delete from the top-level replies
      if (!parentReplyId) {
        comment.replies = comment.replies.filter(reply => reply.id !== replyId);
      } else {
        // Handle nested reply deletion (recursively if needed)
        const deleteNestedReply = (replies, replyToDeleteId, parentId) => {
          for (let i = 0; i < replies.length; i++) {
            const reply = replies[i];
            
            if (reply.id === parentId) {
              // Found the parent, now filter out the reply to delete
              reply.replies = reply.replies.filter(r => r.id !== replyToDeleteId);
              return true;
            }
            
            // Check nested replies
            if (reply.replies && reply.replies.length > 0) {
              const found = deleteNestedReply(reply.replies, replyToDeleteId, parentId);
              if (found) return true;
            }
          }
          
          return false;
        };
        
        deleteNestedReply(comment.replies, replyId, parentReplyId);
      }
      
      // Update the query document
      await updateDoc(queryRef, {
        comments: updatedComments
      });
      
      // Update local state
      setComments(prevComments => {
        const newComments = [...prevComments];
        const commentToUpdate = newComments.find(c => c.id === commentId);
        
        if (commentToUpdate) {
          if (!parentReplyId) {
            // Top-level reply
            commentToUpdate.replies = commentToUpdate.replies.filter(r => r.id !== replyId);
          } else {
            // Nested reply - use the same recursive approach
            const deleteNestedReply = (replies, replyToDeleteId, parentId) => {
              for (let i = 0; i < replies.length; i++) {
                const reply = replies[i];
                
                if (reply.id === parentId) {
                  reply.replies = reply.replies.filter(r => r.id !== replyToDeleteId);
                  return true;
                }
                
                if (reply.replies && reply.replies.length > 0) {
                  const found = deleteNestedReply(reply.replies, replyToDeleteId, parentId);
                  if (found) return true;
                }
              }
              
              return false;
            };
            
            deleteNestedReply(commentToUpdate.replies, replyId, parentReplyId);
          }
        }
        
        return newComments;
      });
      
      toast.success("Reply deleted successfully");
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast.error("Error deleting reply. Please try again.");
    }
  };

  // Handle report functionality
  const handleReportComment = (commentId) => {
    setReportData({
      type: "comment",
      contentId: commentId,
      parentId: id
    });
    setShowReportModal(true);
  };

  const handleReportReply = (commentId, replyId) => {
    setReportData({
      type: "reply",
      contentId: replyId,
      parentId: commentId,
      additionalData: { queryId: id }
    });
    setShowReportModal(true);
  };

  const handleReportQuery = () => {
    setReportData({
      type: "query",
      contentId: id,
      parentId: null
    });
    setShowReportModal(true);
  };

  // Function to directly generate AI response or summary without modal
  const directGenerate = async (feature) => {
    console.log("Directly generating", feature);
    
    // Set the active AI feature for modal to know which generation to perform
    setActiveAIFeature(feature);
    
    // If a trial has already been used for this query AND we're generating a summary
    // Allow direct generation without checking for trial/purchase again
    if (trialUsedForQuery && feature === "summary") {
      console.log("Trial already used for this query, directly generating summary");
      await generateWithFeature(feature);
      return;
    }
    
    // Check if user has access to AI features
    const hasAccess = await hasUnlockedAIFeatures(user?.uid);
    console.log("AI feature access check:", hasAccess);
    
    // If user has purchased access or has AI features unlocked through rewards, proceed directly
    if (hasAccess.isPurchased && hasAccess.expiryDate && new Date(hasAccess.expiryDate) > new Date()) {
      // User has purchased, proceed with generation
      console.log("User has purchased access, proceeding directly");
      await generateWithFeature(feature);
    } else if (hasAccess.hasAccess && !hasAccess.isTrial) {
      // User has access through points or other means (not trial), proceed directly
      console.log("User has AI features unlocked through points, proceeding directly");
      await generateWithFeature(feature);
    } else {
      // User hasn't purchased or unlocked features, show modal for trial/purchase
      console.log("Showing AI usage modal for trial/purchase");
      setAiUsageData(hasAccess);
      setShowAIModal(true);
    }
  };
  
  // Separated function to handle the actual generation after access is confirmed
  const generateWithFeature = async (feature) => {
    if (feature === "response") {
      // Clear any existing state first
      setAiResponse("");
      setShowAiResponse(false);
      
      // Set loading states
      setAiResponseLoading(true);
      setIsGeneratingAI(true);
      
      try {
        const result = await generateAIResponse(
          formatPromptForAI(),
          user?.uid,
          id
        );
        
        if (result.error) {
          toast.error(result.message || "Failed to generate AI response");
          // Clear loading states
          setAiResponseLoading(false);
          setIsGeneratingAI(false);
          return;
        }
        
        // Don't set aiResponse or showAiResponse since we don't want to show it at the top
        // Instead, only save it as a comment
        
        // Save the AI response as a comment
        try {
          // Create new AI comment object
          const aiCommentObj = {
            id: uuidv4(), // Generate a unique ID
            text: result.response,
            upvoteCount: 0,
            downvoteCount: 0,
            votes: {},
            createdAt: new Date().toISOString(),
            commenterName: "AI Assistant",
            commenterPhoto: "",
            createdBy: "ai-system",
            isAIResponse: true,
            replies: [], 
            dataSource: "new" 
          };
          
          // Get the query document
          const queryRef = doc(db, "queries", id);
          const querySnapshot = await getDoc(queryRef);
          
          if (querySnapshot.exists()) {
            // Update the query document with the AI comment
            const queryData = querySnapshot.data();
            const updatedDbComments = [...(queryData.comments || []), aiCommentObj];
            
            await updateDoc(queryRef, {
              comments: updatedDbComments
            });
            
            // Update the local comments state
            const updatedAllComments = [...comments, aiCommentObj];
            setComments(sortComments(updatedAllComments, commentSortOption));
            
            toast.success("AI response added to comments");
            console.log("AI response saved as comment");
          }
        } catch (commentError) {
          console.error("Error saving AI response as comment:", commentError);
          toast.error("Error saving AI response as comment");
        } finally {
          // Make sure loading states are cleared even if comment saving fails
          setAiResponseLoading(false);
          setIsGeneratingAI(false);
        }
      } catch (error) {
        console.error("Error generating AI response:", error);
        toast.error("Failed to generate AI response");
        // Clear loading states
        setAiResponseLoading(false);
        setIsGeneratingAI(false);
      }
    } else if (feature === "summary") {
      // Clear any existing state first
      setCommentSummary("");
      setShowSummary(false);
      
      // Set loading states
      setSummaryLoading(true);
      setIsGeneratingSummary(true);
      
      try {
        console.log("Generating summary with comments:", comments.length, "comments");
        console.log("User ID:", user?.uid);
        console.log("Query title:", queryData?.title || "");
        console.log("Query ID:", id);
        
        const result = await generateAICommentSummary(
          comments,
          user?.uid,
          queryData?.title || "",
          id
        );
        
        console.log("Summary generation result:", result);
        
        if (result.error) {
          toast.error(result.message || "Failed to generate summary");
          // Clear loading states
          setSummaryLoading(false);
          setIsGeneratingSummary(false);
          return;
        }
        
        // Always set the comment summary and show it at the top
        // Use either summary or response property, preferring summary if it exists
        setCommentSummary(result.summary || result.response);
        setShowSummary(true);
        toast.success("Comment summary generated");
      } catch (error) {
        console.error("Error generating comment summary:", error);
        toast.error("Failed to generate comment summary");
      } finally {
        // Always ensure loading states are cleared
        setSummaryLoading(false);
        setIsGeneratingSummary(false);
      }
    }
  };

  // Handle purchase or trial use
  const onPurchase = async (result) => {
    console.log("Purchase result:", result);
    setShowAIModal(false);
    
    if (result.success) {
      // Update UI based on the active feature
      if (activeAIFeature === "response") {
        generateWithFeature('response');
      } else if (activeAIFeature === "summary") {
        generateWithFeature('summary');
      }
      
      // If the result indicates a trial was used, track this for the query
      if (result.trialUsed) {
        setTrialUsedForQuery(true);
        
        // Store in localStorage for persistence
        try {
          // Get existing trial queries
          const storedTrialQueries = localStorage.getItem('ai_trial_queries');
          let trialQueries = storedTrialQueries ? JSON.parse(storedTrialQueries) : [];
          
          // Add this query to the list if not already present
          if (!trialQueries.some(q => q.queryId === id)) {
            trialQueries.push({
              queryId: id,
              timestamp: new Date().toISOString(),
              userId: user?.uid,
              featureType: activeAIFeature // Track which feature used the trial
            });
            
            // Store back to localStorage
            localStorage.setItem('ai_trial_queries', JSON.stringify(trialQueries));
            console.log(`Trial usage for query ${id} stored in localStorage`);
          }
        } catch (error) {
          console.error("Error storing trial usage in localStorage:", error);
        }
      }
    } else {
      toast.error(result.message || "Failed to process request");
    }
  };

  // Helper function for updating vote counts in comments and replies
  const updateVoteCount = (item, voteType, userId) => {
    const prevVote = item.votes?.[userId];
    const votes = { ...(item.votes || {}) };
    
    if (prevVote === voteType) {
      // Cancel the vote if clicking the same button
      delete votes[userId];
      return {
        ...item,
        votes,
        upvoteCount: voteType === "upvote" 
          ? Math.max(0, (item.upvoteCount || 0) - 1) 
          : (item.upvoteCount || 0),
        downvoteCount: voteType === "downvote" 
          ? Math.max(0, (item.downvoteCount || 0) - 1) 
          : (item.downvoteCount || 0),
      };
    } else {
      // Set the new vote
      votes[userId] = voteType;
      
      // Adjust upvote count
      let upvoteCount = item.upvoteCount || 0;
      if (voteType === "upvote") {
        upvoteCount++;
      } else if (prevVote === "upvote") {
        upvoteCount = Math.max(0, upvoteCount - 1);
      }
      
      // Adjust downvote count
      let downvoteCount = item.downvoteCount || 0;
      if (voteType === "downvote") {
        downvoteCount++;
      } else if (prevVote === "downvote") {
        downvoteCount = Math.max(0, downvoteCount - 1);
      }
      
      return {
        ...item,
        votes,
        upvoteCount,
        downvoteCount,
      };
    }
  };

  // Helper function to increment user's comment count for points
  const incrementUserCommentCount = async (userId) => {
    if (!userId) return;
    
    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        await updateDoc(userRef, {
          commentCount: increment(1),
          points: increment(1)
        });
      }
    } catch (error) {
      console.error("Error updating user points:", error);
    }
  };

  // Handle edit query
  const handleEdit = () => {
    setShowEditModal(true)
  }

  // Handle delete query
  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this query? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "queries", id))
        toast.success("Query deleted successfully!")
        navigate("/")
      } catch (err) {
        console.error("Error deleting query:", err)
        toast.error("Failed to delete query")
      }
    }
  }

  // Handle save after edit
  const handleSave = async (updatedData) => {
    try {
      const queryDoc = await getDoc(doc(db, "queries", id))
      if (queryDoc.exists()) {
        setQueryData({ id: queryDoc.id, ...updatedData })
      }
      setShowEditModal(false)
      toast.success("Query updated successfully")
    } catch (err) {
      console.error("Error saving query:", err)
      toast.error("Failed to save query")
    }
  }

  // Format query data for AI input
  const formatPromptForAI = () => {
    // Extract query title and description
    const title = queryData?.title || "No title";
    const description = queryData?.description || "No description";
    
    // Format comments for AI processing
    const commentText = comments.map(comment => {
      // Skip AI responses from being included
      if (comment.isAIResponse) return null;
      
      let commentStr = `${comment.commenterName || 'Anonymous'}: "${comment.text}" [Votes: ${comment.upvoteCount || 0}]`;
      
      // Include replies if any
      if (comment.replies && comment.replies.length > 0) {
        commentStr += "\n  Replies:";
        comment.replies.forEach(reply => {
          commentStr += `\n    - ${reply.commenterName || 'Anonymous'}: "${reply.text}" [Votes: ${reply.upvoteCount || 0}]`;
        });
      }
      
      return commentStr;
    }).filter(Boolean).join('\n\n');
    
    // Build the complete prompt
    return `Query Title: ${title}\n\nDescription: ${description}\n\nComments:\n${commentText}\n\nPlease provide a thorough but concise analysis and response addressing the query. Include:
    
    1. Response to Query: ${title}
    2. Analysis of what the user is asking
    3. Key insights or solutions (in brief sections)
    
    Keep your response well-structured but compact. Use just enough formatting for clarity.`;
  };

  return (
    <div className="query-details-container">
      <Navbar />
      
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading query details...</p>
          {showResetButton && (
            <div className="reset-container">
              <p>Taking longer than expected...</p>
              <button 
                className="reset-button"
                onClick={handleManualReset}
              >
                Reset Page
              </button>
            </div>
          )}
        </div>
      ) : !queryData || queryData.error ? (
        <div className="error-container">
          <h2>Could not load query</h2>
          <p>{queryData?.error === "timeout" 
              ? "The query took too long to load. Please try again." 
              : "This query could not be found or may have been deleted."}</p>
          <button 
            className="return-home-button"
            onClick={() => navigate("/")}
          >
            Return to Home
          </button>
        </div>
      ) : (
        <>
          <QueryHeader 
            queryData={{
              ...queryData,
              crowdfundingSummary: crowdfundingDetails
            }}
            handleImageClick={handleImageClick}
            onReport={handleReportQuery}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isOwner={userId === queryData?.createdBy}
          />
          
          {/* Kangaroo Court: Sarcastic AI Magistrate Ruling */}
          <AIJudgeBanner 
            caseId={id} 
            ruling={queryData?.aiJudgeRuling} 
            onRulingRendered={(ruling) => setQueryData(prev => ({ ...prev, aiJudgeRuling: ruling }))} 
          />

          {/* Kangaroo Court: Peer Jury Deliberation & Verdict Voting */}
          <VerdictChips 
            caseId={id} 
            verdictCounts={queryData?.verdictCounts || {}} 
            totalVotes={queryData?.totalVotes || 0}
            onVoteSuccess={async () => {
              try {
                const docRef = doc(db, 'cases', id);
                let snap = await getDoc(docRef);
                if (!snap.exists()) {
                  snap = await getDoc(doc(db, 'queries', id));
                }
                if (snap.exists()) {
                  setQueryData(prev => ({ ...prev, ...snap.data() }));
                }
              } catch (e) {
                console.log(e);
              }
            }}
          />
          
          <div className="content-section">
            <div className="vote-controls">
              <button
                className={`vote-btn ${queryData.votes && queryData.votes[userId] === "upvote" ? "active" : ""}`}
                onClick={() => handleVote("upvote")}
                disabled={!userId}
              >
                <ThumbsUp size={18} />
                <span>{queryData.upvoteCount || 0}</span>
              </button>
              <button
                className={`vote-btn ${queryData.votes && queryData.votes[userId] === "downvote" ? "active" : ""}`}
                onClick={() => handleVote("downvote")}
                disabled={!userId}
              >
                <ThumbsDown size={18} />
                <span>{queryData.downvoteCount || 0}</span>
              </button>
            </div>
            
            <div className="ai-buttons">
                <button 
                  className="ai-button"
                  onClick={() => directGenerate('response')}
                  disabled={isGeneratingAI || !userId || comments.some(comment => comment.isAIResponse)}
                  title={comments.some(comment => comment.isAIResponse) ? "Only one AI response is allowed per query" : ""}
                >
                  {isGeneratingAI ? 
                    <div className="ai-loading-animation" aria-hidden="true"></div> : 
                    <Bot size={16} />
                  }
                  <span>Generate AI Response</span>
                </button>
                <button 
                  className="ai-button summary-button" 
                  onClick={() => directGenerate('summary')}
                  disabled={isGeneratingSummary || comments.length === 0 || !userId}
                >
                  {isGeneratingSummary ? 
                    <div className="ai-loading-animation" aria-hidden="true"></div> : 
                    <ClipboardList size={16} />
                  }
                  <span>Generate Comment Summary</span>
                </button>
              </div>
            
            {/* Global loading indicator */}
            <div className={`ai-loading-global ${isGeneratingAI || isGeneratingSummary ? 'active' : ''}`}>
              <div className="spinner"></div>
              <span>
                {isGeneratingAI 
                  ? "Generating AI response as a comment..." 
                  : isGeneratingSummary 
                    ? "Generating AI summary of comments..." 
                    : ""}
              </span>
            </div>
            
            {/* AI Response Display - REMOVED */}
            {/* Instead, show only the Comment Summary */}
            
            {showSummary && commentSummary && (
              <div className="ai-summary">
                <div className="ai-summary-header">
                  <CommunityAIIcon size={20} />
                  <h3>AI Comment Summary</h3>
                </div>
                <div className="summary-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      code({node, inline, className, children, ...props}) {
                        return inline ? 
                          <code className={className} {...props}>{children}</code> : 
                          <pre className="code-block">
                            <code className={className} {...props}>{children}</code>
                          </pre>
                      }
                    }}
                  >
                    {commentSummary}
                  </ReactMarkdown>
                </div>
              </div>
            )}
            
            <div className="comments-section">
              <div className="comments-header">
                <div className="comments-header-left">
                  <MessageCircle size={20} />
                  <h2>Comments<span className="comment-count">({comments.length})</span></h2>
                </div>
                <div className="comments-sort">
                  <Filter size={14} />
                  <select 
                    className="comment-sort-select"
                    value={commentSortOption}
                    onChange={handleSortChange}
                  >
                    <option value="mostLiked">Most Liked</option>
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="engagement">Most Replies</option>
                  </select>
                </div>
              </div>
              
              <div className="comment-input-container">
                <textarea
                  className="comment-input"
                  placeholder={userId ? "Add a comment..." : "Sign in to comment"}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={!userId}
                />
                <div className="comment-input-actions">
                  <button
                    className="post-comment-btn"
                    onClick={handlePostComment}
                    disabled={!newComment.trim() || !userId || isPostingComment}
                  >
                    {isPostingComment ? (
                      <>
                        <div className="loading-spinner"></div>
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Post Comment
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="comment-list">
                {comments.length === 0 ? (
                  <div className="no-comments">
                    <div className="no-comments-icon">
                      <MessageCircle size={24} />
                    </div>
                    <p>No comments yet. Be the first to share your thoughts!</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      userId={userId}
                      handleCommentVote={handleCommentVote}
                      handleEditComment={handleEditComment}
                      handleDeleteComment={handleDeleteComment}
                      handleReportComment={handleReportComment}
                      handlePostReply={handlePostReply}
                      handleReplyVote={handleReplyVote}
                      handleEditReply={handleEditReply}
                      handleDeleteReply={handleDeleteReply}
                      handleReportReply={handleReportReply}
                      setReplyingCommentId={setReplyingCommentId}
                      setReplyText={setReplyText}
                      replyingCommentId={replyingCommentId}
                      replyText={replyText}
                      editingCommentId={editingCommentId}
                      setEditingCommentId={setEditingCommentId}
                      editText={editText}
                      setEditText={setEditText}
                      editingReplyId={editingReplyId}
                      setEditingReplyId={setEditingReplyId}
                      editReplyText={editReplyText}
                      setEditReplyText={setEditReplyText}
                      activeDropdown={activeDropdown}
                      setActiveDropdown={setActiveDropdown}
                      parentReplyId={parentReplyId}
                      setParentReplyId={setParentReplyId}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
      
      {activeImage && (
        <div className="image-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={activeImage} alt="Full size attachment" />
            <button className="close-modal" onClick={forceCloseModal}>
              <X size={24} />
            </button>
          </div>
        </div>
      )}
      
      {showReportModal && reportData && (
        <ReportModal
          {...reportData}
          onClose={() => {
            setShowReportModal(false);
            setReportData(null);
          }}
        />
      )}

      {showAIUsageModal && aiUsageData && (
        <AIUsageModal
          userId={userId}
          usageData={{
            trialsRemaining: aiUsageData?.trialsRemaining || 0,
            trialUsageCount: aiUsageData?.trialUsageCount || 0,
            isPurchased: aiUsageData?.isPurchased || false,
            expiryDate: aiUsageData?.expiryDate,
            source: aiUsageData?.source || '',
            isAdminGranted: aiUsageData?.isAdminGranted || false
          }}
          onClose={() => {
            setShowAIUsageModal(false);
            setAiUsageData(null);
          }}
          onPurchase={(result) => {
            setShowAIUsageModal(false);
            setActiveAIFeature(null);
            onPurchase(result);
          }}
          userPoints={userPoints}
        />
      )}

      {showEditModal && (
        <EditQueryModal
          query={queryData}
          onClose={() => setShowEditModal(false)}
          onSave={handleSave}
        />
      )}

      {showAIModal && (
        <AIUsageModal
          userId={userId}
          usageData={{
            trialsRemaining: aiUsageData?.trialsRemaining || 0,
            trialUsageCount: aiUsageData?.trialUsageCount || 0,
            isPurchased: aiUsageData?.isPurchased || false,
            expiryDate: aiUsageData?.expiryDate,
            source: aiUsageData?.source || '',
            isAdminGranted: aiUsageData?.isAdminGranted || false
          }}
          onClose={() => {
            setShowAIModal(false);
            setActiveAIFeature(null);
          }}
          onPurchase={(result) => {
            setShowAIModal(false);
            setActiveAIFeature(null);
            onPurchase(result);
          }}
          userPoints={userPoints}
        />
      )}
    </div>
  )
}

export default QueryDetails


